import { Badge } from "./ui/badge";
import { History, ExternalLink, Heart, MessageCircle, Share2, Eye, TrendingUp, TrendingDown, Minus } from "lucide-react";

// Publish History panel for PostDetailModal — lists every send (current live
// first, then reverse-chrono history) with per-send engagement stats and a
// delta chip vs. the previous send so admins can eyeball whether a re-send
// actually lifted reach.
export default function PublishHistoryPanel({ post }) {
  const history = post.publish_history || [];
  const current = post.review_status === "published"
    ? {
        external_post_id: post.external_post_id,
        permalink_url: post.permalink_url,
        published_at: post.published_at,
        routed_channels: post.routed_channels,
        real_engagement: post.real_engagement,
        current: true,
      }
    : null;
  // Chronological order: earliest send first at the bottom of the array,
  // newest live send at the top. We compute deltas newest-vs-just-below.
  const timeline = [current, ...[...history].reverse()].filter(Boolean);

  if (timeline.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center space-y-2" data-testid={`detail-history-empty-${post.id}`}>
        <History size={22} className="mx-auto text-muted-foreground" />
        <p className="text-sm font-semibold">No publish history yet</p>
        <p className="text-xs text-muted-foreground">Once this post has been sent, every re-send will show up here with its engagement stats.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`detail-history-panel-${post.id}`}>
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-2">
        <History size={12} /> {timeline.length} send{timeline.length !== 1 ? "s" : ""} — engagement snapshots freeze on re-send, current live send updates every hour.
      </div>
      <ol className="space-y-3">
        {timeline.map((entry, i) => {
          const prev = timeline[i + 1];
          return <SendCard key={`${entry.external_post_id || i}-${i}`}
                          entry={entry} prev={prev}
                          label={entry.current ? "Current live send" : `Send #${timeline.length - i}`}
                          postId={post.id} idx={i} />;
        })}
      </ol>
    </div>
  );
}

const StatRow = ({ Icon, label, value, prevValue, testid }) => {
  if (value == null && prevValue == null) return null;
  const v = value ?? 0;
  const pv = prevValue ?? 0;
  const delta = prevValue == null || value == null ? null : v - pv;
  return (
    <div className="flex items-center justify-between text-xs" data-testid={testid}>
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon size={12} /> {label}
      </span>
      <span className="flex items-center gap-2">
        <span className="font-semibold tabular-nums">{v.toLocaleString()}</span>
        {delta != null && delta !== 0 && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
            delta > 0 ? "bg-emerald-500/15 text-emerald-700" : "bg-rose-500/15 text-rose-700"
          }`}>
            {delta > 0 ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
            {delta > 0 ? "+" : ""}{delta.toLocaleString()}
          </span>
        )}
        {delta === 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-muted text-muted-foreground">
            <Minus size={9} /> flat
          </span>
        )}
      </span>
    </div>
  );
};

const SendCard = ({ entry, prev, label, postId, idx }) => {
  const eng = entry.real_engagement || {};
  const prevEng = prev?.real_engagement || {};
  const when = entry.published_at ? new Date(entry.published_at) : null;
  const hasStats = eng.likes != null || eng.comments != null || eng.plays != null || eng.reach != null;
  return (
    <li className="rounded-lg border bg-card p-3 space-y-2" data-testid={`detail-history-entry-${postId}-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{label}</span>
            {entry.current && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Live</Badge>}
          </div>
          {when && (
            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`detail-history-when-${postId}-${idx}`}>
              {when.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          )}
          {entry.external_post_id && (
            <p className="text-[10px] text-muted-foreground font-mono mt-0.5">id · {entry.external_post_id}</p>
          )}
        </div>
        {entry.permalink_url && (
          <a href={entry.permalink_url} target="_blank" rel="noreferrer"
            className="text-[10px] text-primary hover:text-primary/80 flex items-center gap-0.5 shrink-0 mt-0.5"
            data-testid={`detail-history-link-${postId}-${idx}`}>
            Open <ExternalLink size={9} />
          </a>
        )}
      </div>
      {hasStats ? (
        <div className="space-y-1 pt-1 border-t border-dashed">
          <StatRow Icon={Heart} label="Likes" value={eng.likes} prevValue={prevEng.likes} testid={`detail-history-likes-${postId}-${idx}`} />
          <StatRow Icon={MessageCircle} label="Comments" value={eng.comments} prevValue={prevEng.comments} testid={`detail-history-comments-${postId}-${idx}`} />
          <StatRow Icon={Share2} label="Shares" value={eng.shares} prevValue={prevEng.shares} testid={`detail-history-shares-${postId}-${idx}`} />
          <StatRow Icon={Eye} label="Reach" value={eng.reach} prevValue={prevEng.reach} testid={`detail-history-reach-${postId}-${idx}`} />
          <StatRow Icon={Eye} label="Plays" value={eng.plays} prevValue={prevEng.plays} testid={`detail-history-plays-${postId}-${idx}`} />
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground italic pt-1 border-t border-dashed">
          Engagement stats sync every hour — freshly-sent posts show numbers within an hour of going live.
        </p>
      )}
    </li>
  );
};
